import {Injectable} from "@angular/core";
import {environment} from "../../../environments/environment";
import {arrayRemove, arrayUpsert} from "@datorama/akita";
import {HttpClient, HttpParams} from "@angular/common/http";
import {AuthService} from "@auth0/auth0-angular";
import {CustomerData, CustomerState, CustomerStore} from "./customer.store";
import {
  catchError,
  defer,
  EMPTY, expand,
  filter,
  finalize,
  first,
  last,
  map,
  Observable,
  switchMap,
  tap,
  timeout
} from "rxjs";
import {Customer, CustomerActivity} from "../../data-access/api-customers/Customer";
import {CustomerDocument} from "../../data-access/api-customers/CustomerDocument";
import {retryBackoff} from "../../data-access/retry.service";
import {PageResponse} from "../../data-access/PageResponse";


@Injectable({ providedIn: 'root' })
export class CustomerStoreService {
  private readonly baseUrl: string;
  private apiV1: string = environment.apiV1;
  private baseRef: string = environment.origin;
  private serviceCore: string = environment.service.core;

  constructor(
    private store: CustomerStore,
    private httpClient: HttpClient,
    private authService: AuthService,
  ) {
    this.baseUrl = environment.local;
  }

  public setLoading(isLoading: boolean): void {
    this.store.setLoading(isLoading);
  }

  public init(): void {
    this.setLoading(true);

    this.authService.isLoading$
      .pipe(
        // wait until SDK finished init/callback
        filter((loading: boolean) => !loading),
        switchMap(() => this.authService.isAuthenticated$),
        first(),
        // safe: token is ready, interceptor will attach it
        switchMap((isAuth: boolean) => {
          if (!isAuth) {
            this.authService.loginWithRedirect({ appState: { target: '/services'} });
            return EMPTY;
          }
          return this.loadPriorityThenShadow$();
        }),
        finalize(() => this.setLoading(false)),
        catchError((err) => {
          console.error('[bootstrap customers] init failed', err);
          return EMPTY;
        })
      ).subscribe();
  }

  private loadPriorityThenShadow$(): Observable<void> {
    return this.loadPriority$(500).pipe(
      tap(() => {
        // this.loadShadowAll$(100).subscribe();
      }),
      map(() => void 0)
    );
  }

  private loadPriority$(size: number): Observable<any> {
    return this.getPaginateCustomers({ page: 0, size, sort: 'dateUpd,desc' })
      .pipe(
        timeout({ first: 8000 }),
        // retry léger sur call critique
        retryBackoff(1, 400),
        tap((res: PageResponse<Customer>) => {
          this.store.update((state: CustomerState) => ({
            ...state,
            customers: this.mergeCustomers(state.customers, res.items),
            customerPage: 0,
            customerTotalItems: res.totalItems,
            customerTotalPage: res.totalPages,
            error: undefined
          }));
          console.log('[bootstrap customers] all datasets loaded');
        }),
        catchError((error) => {
          this.store.setError(error);
          return EMPTY;
        })
      );
  }

  public loadShadowNextPage$(size: number): Observable<PageResponse<Customer>> {
    const state: CustomerState = this.store.getValue();
    if (!state.customerHasNext) {
      return EMPTY;
    }

    const page: number = state.customerPage + 1;

    return this.getPaginateCustomers({ page, size, sort: 'dateUpd,desc' })
      .pipe(
        timeout({ first: 8000 }),
        retryBackoff(2, 400),
        tap((res: PageResponse<Customer>) => {
          const mergedData: Customer[] = this.mergeCustomers(state.customers, res.items);
          this.store.update((state: CustomerState) => ({
            customers: mergedData,
            customerPage: page,
            customerHasNext: res.hasNext,
            customerTotalItems: res.totalItems,
            error: undefined
          }));
          console.log(mergedData.length);
        }),
        catchError((error) => {
          this.store.setError(error);
          return EMPTY;
        })
      );
  }

  // private loadShadowAll$(size: number): Observable<void> {
    // return defer(() => this.loadShadowNextPage$(size)).pipe(
    //   expand(() => {
    //     const state = this.store.getValue();
    //     return state.customerHasNext ? this.loadShadowNextPage$(size) : EMPTY;
    //   }),
    //   last(undefined, undefined),
    //   map(() => void 0)
    // );
  // }

  private getPaginateCustomers(params: { page?: number; size?: number; sort?: string; }): Observable<PageResponse<Customer>> {
    const url: string = `${this.baseRef}${this.serviceCore}${this.apiV1}customers/pageable`;

    let httpParams = new HttpParams()
      .set('page', String(params.page ?? 0))
      .set('size', String(params.size ?? 50))
      .set('sort', params.sort ?? 'dateUpd,desc');
    return this.httpClient.get<PageResponse<Customer>>(url, { params: httpParams });
  }

  private mergeCustomers(existing: Customer[], incoming: Customer[]): Customer[] {
    const map = new Map<string, Customer>();
    for (const b of existing) map.set(b.id, b);
    for (const b of incoming) map.set(b.id, b);

    return Array.from(map.values());
  }

  /**
   * UPSERT & DELETE METHODS
   *
   *
   *
   */

  public upsertCustomer(customer: Customer): void {
    this.store.update((state: CustomerData) => {
      const customers: Customer[] = arrayUpsert(state.customers, customer.id, customer);
      return {
        ...state,
        customers
      };
    });
  }

  public async deleteCustomer(customerId: string): Promise<boolean> {
    return new Promise<boolean>((resolve => {
      this.store.update((state: CustomerData) => {
        const customers: Customer[] = arrayRemove(state.customers, customerId);
        return {
          ...state,
          customers
        };
      });
      resolve(true);
    }));
  }

  public upsertCustomerActivity(customerId: string, customerActivity: CustomerActivity, activityId?: string): void {
    const customer: Customer | undefined = this.store.getValue().customers
      .find((cus: Customer) => cus.id === customerId);

    if (customer) {
      const newCustomer: Customer = { ...customer };
      const newCustomerActivities: CustomerActivity[] = customer.customerActivities && customer.customerActivities.length
        ? [ ...customer.customerActivities ]
        : [];

      if (activityId) {
        const index: number = newCustomerActivities
          .findIndex((activity: CustomerActivity) => activity.id === customerActivity.id);
        if (index > -1) {
          newCustomerActivities[index] = { ...customerActivity };
        }
      } else {
        newCustomerActivities.push(customerActivity);
      }
      newCustomer.customerActivities = [ ...newCustomerActivities ];
      this.upsertCustomer(newCustomer);
    }
  }

  public deleteCustomerActivity(customerId: string, activityId: string): void {
    const customer: Customer | undefined = this.store.getValue().customers
      .find((cus: Customer) => cus.id === customerId);

    if (customer) {
      const newCustomer: Customer = { ...customer };
      const newCustomerActivities: CustomerActivity[] = customer.customerActivities && customer.customerActivities.length
        ? [ ...customer.customerActivities]
        : [];
      const index: number = newCustomerActivities
        .findIndex((activity: CustomerActivity) => activity.id === activityId);
      newCustomerActivities.splice(index, 1);
      newCustomer.customerActivities = [ ...newCustomerActivities ];
      this.upsertCustomer(newCustomer);
    }
  }

  public updateCustomerDocument(customer: Customer, customerDocument: CustomerDocument): void {
    this.store.update((state: CustomerData) => {
      let existingDocs: CustomerDocument[] = customer.customerDocuments
        ? [ ...customer.customerDocuments ]
        : [];

      console.log(existingDocs);

      const index: number = existingDocs.findIndex((doc: CustomerDocument) =>
        doc.id === customerDocument.id);

      const newCustomerDocuments: CustomerDocument[] =
        index === -1
          ? [...existingDocs, customerDocument]
          : existingDocs.map((doc, i) => (i === index ? { ...customerDocument } : doc));

      const newCustomer: Customer = { ...customer };
      newCustomer.customerDocuments = [ ...newCustomerDocuments ];

      console.log(newCustomer.customerDocuments);

      const customers: Customer[] = arrayUpsert(state.customers, newCustomer.id, newCustomer);
      return {
        ...state,
        customers
      };
    });
  }

  public deleteCustomerDocument(customerId: string, documentId: string): void {
    this.store.update((state: CustomerData) => {
      const customer: Customer | undefined = state.customers
        .find((customer: Customer) => customer.id === customerId);

      let currentCustomerDocuments: CustomerDocument[] = [];

      if (customer) {
        currentCustomerDocuments = customer.customerDocuments
          ? [...customer.customerDocuments]
          : [];

        const index: number = currentCustomerDocuments.findIndex((doc: CustomerDocument) =>
          doc.id === documentId);

        if (index !== -1) {
          currentCustomerDocuments.splice(index, 1);
        }

        const newCustomer: Customer = { ...customer };
        newCustomer.customerDocuments = [ ...currentCustomerDocuments ];

        this.upsertCustomer(newCustomer);
      }
    });
  }

}
